import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function BlogPage() {
  const { getTotalItems } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts', icon: 'ri-article-line' },
    { id: 'gaming', name: 'Gaming News', icon: 'ri-gamepad-line' },
    { id: 'fortnite', name: 'Fortnite Updates', icon: 'ri-trophy-line' },
    { id: 'guides', name: 'Guides & Tips', icon: 'ri-book-line' },
    { id: 'deals', name: 'Deals & Offers', icon: 'ri-price-tag-line' }
  ];

  const blogPosts = [
    {
      id: 1,
      category: 'fortnite',
      title: 'Fortnite Chapter 5 Season 1: Everything You Need to Know',
      excerpt: 'Discover all the new features, map changes, and exciting content coming to Fortnite Chapter 5 Season 1, including new weapons, locations, and Battle Pass rewards.',
      author: 'Gaming Team',
      date: '2024-01-15',
      readTime: '5 min read',
      image: 'https://public.readdy.ai/ai/img_res/1d2e0165efbb1a48badfc27cf10d1cf9.jpg',
      featured: true
    },
    {
      id: 2,
      category: 'guides',
      title: 'How to Get the Most V-Bucks for Your Money',
      excerpt: 'Learn the best strategies for purchasing V-Bucks, including seasonal offers, bundle deals, and the most cost-effective packages for different spending levels.',
      author: 'Deal Hunter',
      date: '2024-01-12',
      readTime: '3 min read',
      image: 'https://readdy.ai/api/search-image?query=fortnite%20v-bucks%20currency%20golden%20coins%20digital%20gaming%20money%20epic%20games%20store%20purchase%20guide&width=600&height=300&seq=blog2&orientation=landscape',
      featured: false
    },
    {
      id: 3,
      category: 'gaming',
      title: 'Epic Games Store Free Games This Month',
      excerpt: 'Check out this month\'s free games on Epic Games Store and learn how to claim them. Don\'t miss out on these amazing titles available at no cost.',
      author: 'Free Games Alert',
      date: '2024-01-10',
      readTime: '4 min read',
      image: 'https://readdy.ai/api/search-image?query=epic%20games%20store%20free%20games%20collection%20digital%20library%20gaming%20platform%20colorful%20game%20covers%20promotional%20banner&width=600&height=300&seq=blog3&orientation=landscape',
      featured: false
    },
    {
      id: 4,
      category: 'deals',
      title: 'Best Gaming Gift Card Deals for 2024',
      excerpt: 'Discover the top gaming gift card deals and promotions for 2024. Save money on your favorite games and in-game content with these exclusive offers.',
      author: 'Savings Expert',
      date: '2024-01-08',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=gaming%20gift%20cards%20collection%20epic%20games%20steam%20playstation%20xbox%20colorful%20cards%20promotional%20deals%20savings&width=600&height=300&seq=blog4&orientation=landscape',
      featured: false
    },
    {
      id: 5,
      category: 'fortnite',
      title: 'Fortnite Crew Subscription: Is It Worth It?',
      excerpt: 'A comprehensive review of the Fortnite Crew subscription service, including monthly benefits, exclusive content, and whether it provides good value for money.',
      author: 'Subscription Review',
      date: '2024-01-05',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=fortnite%20crew%20subscription%20exclusive%20content%20battle%20pass%20v-bucks%20monthly%20rewards%20premium%20gaming%20service&width=600&height=300&seq=blog5&orientation=landscape',
      featured: false
    },
    {
      id: 6,
      category: 'guides',
      title: 'Complete Guide to Redeeming Game Codes',
      excerpt: 'Step-by-step instructions for redeeming game codes on different platforms including Epic Games, Steam, PlayStation, Xbox, and Nintendo Switch.',
      author: 'Tech Support',
      date: '2024-01-03',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=game%20code%20redemption%20guide%20multiple%20gaming%20platforms%20epic%20games%20steam%20playstation%20xbox%20tutorial%20instructions&width=600&height=300&seq=blog6&orientation=landscape',
      featured: false
    }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=modern%20gaming%20blog%20website%20with%20articles%20news%20updates%20purple%20lighting%20digital%20content%20creation%20gaming%20journalism%20professional%20environment&width=1200&height=600&seq=blog-hero&orientation=landscape')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Gaming <span className="text-yellow-400">Blog</span>
            </h1>
            <p className="text-xl text-purple-200 mb-8 max-w-3xl mx-auto">
              Stay updated with the latest gaming news, Fortnite updates, exclusive deals, and helpful guides from our expert team.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <i className={`${category.icon} text-sm`}></i>
                <span className="text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && selectedCategory === 'all' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative">
                  <img 
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-64 lg:h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase">
                      {categories.find(c => c.id === featuredPost.category)?.name}
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>{featuredPost.author}</span>
                      <span>•</span>
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{featuredPost.readTime}</span>
                    </div>
                    <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                      Read More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(selectedCategory === 'all' ? regularPosts : filteredPosts).map((post) => (
              <article key={post.id} className="bg-gray-800 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-transform duration-300">
                <div className="relative">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase">
                      {categories.find(c => c.id === post.category)?.name}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-bold text-lg mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <div>{post.author}</div>
                      <div>{new Date(post.date).toLocaleDateString()}</div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {post.readTime}
                    </div>
                  </div>
                  <button className="w-full mt-4 bg-gray-700 hover:bg-purple-600 text-white py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                    Read Article
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-purple-100 mb-8">
            Subscribe to our newsletter and never miss the latest gaming news, deals, and exclusive content.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-0 focus:outline-none text-sm"
            />
            <button className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* Popular Tags */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Popular Tags</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {['Fortnite', 'V-Bucks', 'Battle Pass', 'Epic Games', 'Gaming Deals', 'Free Games', 'Gift Cards', 'Game Codes', 'Gaming News', 'Tutorials'].map((tag, index) => (
              <span 
                key={index}
                className="bg-gray-700 hover:bg-purple-600 text-gray-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}